import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { launchViaAgent, uninstallViaAgent } from "../utils/agentInstaller";
import { extractProjectGroupId } from "../utils/packageHelpers";
import { SPATIAL_STUDIO_AGENT_ID, DESKTOP_PLATFORMS } from "../constants";
import type { AgentInfo, AgentInstalledApp } from "../hooks/useAgentConnection";
import {
  fetchAllCurrentElements,
  fetchSpecificProjectGroupWithProjects,
  fetchProjectGroupMedias,
  getProjectGroupPermissionsFromServer,
  setCurrentProjectGroupPermissions,
} from "../../../features/projectGroups/state/projectGroupsSlice";
import { getBuilds } from "../../../features/builds/state/buildsSlice";
import { IBuild } from "../../../model/model";
import type { AppDispatch } from "../../../common/state/store";
import LaunchButton from "./LaunchButton";
import AgentUninstallButton from "./AgentUninstallButton";
import { CheckIcon, WarningCircleIcon } from "./icons";

// ── App row ──────────────────────────────────────────────

function AppRow({
  app,
  latestBuild,
  onUninstalled,
  onRemoveApp,
}: {
  app: AgentInstalledApp;
  latestBuild: IBuild | null;
  onUninstalled: () => void;
  onRemoveApp?: (appId: string) => void;
}) {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const projectGroupId = latestBuild?.project_group?.id ?? extractProjectGroupId(app.packageName);
  const latestVersion = latestBuild?.version ?? null;
  const installedVersion = parseInt(app.version, 10);
  const isOutdated =
    latestVersion !== null && !isNaN(installedVersion) && installedVersion < latestVersion;

  const handleNavigateToProjectGroup = async () => {
    if (!projectGroupId) return;
    await dispatch(
      fetchSpecificProjectGroupWithProjects({
        projectGroupId,
        componentId: "projectGroupListComponent",
      }),
    );
    dispatch(
      fetchAllCurrentElements({
        projectGroupId,
        componentId: "projectGroupListComponent",
      }),
    );
    dispatch(
      fetchProjectGroupMedias({
        projectGroupId,
        componentId: "projectGroupMediasComponentId",
      }),
    );
    dispatch(getProjectGroupPermissionsFromServer({ projectGroupId })).then((res) => {
      dispatch(setCurrentProjectGroupPermissions((res.payload as any).data));
    });
    navigate(`/courses/${projectGroupId}/list`);
  };

  return (
    <tr className="border-b last:border-0">
      <td className="py-3 px-4">
        {projectGroupId ? (
          <button
            onClick={handleNavigateToProjectGroup}
            className="font-medium text-blue-600 hover:underline cursor-pointer text-left"
          >
            {app.displayName}
          </button>
        ) : (
          <div className="font-medium">{app.displayName}</div>
        )}
        <div className="text-xs text-gray-400">{app.packageName}</div>
      </td>
      <td className="py-3 px-4">V{app.version}</td>
      <td className="py-3 px-4">{latestVersion !== null ? `V${latestVersion}` : "—"}</td>
      <td className="py-3 px-4">
        {isOutdated ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-orange-50 text-orange-600 border border-orange-200">
            <WarningCircleIcon className="w-3 h-3" />
            {t("deviceManager.outdated")}
          </span>
        ) : latestVersion !== null ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-50 text-green-600 border border-green-200">
            <CheckIcon className="w-3 h-3" />
            {t("deviceManager.upToDate")}
          </span>
        ) : (
          <span className="text-xs text-gray-400">{t("deviceManager.unknown")}</span>
        )}
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <LaunchButton onLaunch={() => launchViaAgent(app.id)} />
          {/* Media management for desktop — disabled for now */}
          <AgentUninstallButton
            onUninstall={() => uninstallViaAgent(app.id)}
            onSuccess={() => {
              onRemoveApp?.(app.id);
              onUninstalled();
            }}
          />
        </div>
        {/* Media push modal for desktop — disabled for now */}
      </td>
    </tr>
  );
}

// ── Main table ───────────────────────────────────────────

interface Props {
  agentInfo: AgentInfo;
  onRefresh?: () => void;
  onRemoveApp?: (appId: string) => void;
}

export default function DesktopAppsTable({ agentInfo, onRefresh, onRemoveApp }: Props) {
  const { t } = useTranslation();
  const builds = useSelector(getBuilds) as IBuild[];

  // Build lookup: latest published desktop build per package_name OR per projectGroupId
  // PC builds often lack package_name, so we also index by project_group.id
  const latestBuildByPackage: Record<string, IBuild> = {};
  const latestBuildByPgId: Record<number, IBuild> = {};
  for (const build of builds) {
    if (build.status !== "Published") continue;
    if (!DESKTOP_PLATFORMS.includes(build.target_platform)) continue;
    if (build.package_name) {
      const existing = latestBuildByPackage[build.package_name];
      if (!existing || build.version > existing.version) {
        latestBuildByPackage[build.package_name] = build;
      }
    }
    const pgId = build.project_group?.id;
    if (pgId) {
      const existing = latestBuildByPgId[pgId];
      if (!existing || build.version > existing.version) {
        latestBuildByPgId[pgId] = build;
      }
    }
  }

  /** Find the best matching build for an installed agent app */
  const findLatestBuild = (app: AgentInstalledApp): IBuild | null => {
    // 1. Direct package_name match
    if (latestBuildByPackage[app.packageName]) return latestBuildByPackage[app.packageName];
    // 2. Fallback: extract projectGroupId from the synthetic package name
    const pgId = extractProjectGroupId(app.packageName);
    if (pgId && latestBuildByPgId[pgId]) return latestBuildByPgId[pgId];
    return null;
  };

  // Filter out Spatial Studio (it has its own dedicated section)
  const otherApps = agentInfo.installedApps.filter((a) => a.id !== SPATIAL_STUDIO_AGENT_ID);

  if (otherApps.length === 0) {
    return (
      <div className="mt-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          {t("deviceManager.installedApps", { count: 0 })}
        </h2>
        <p className="text-gray-400 text-sm italic">{t("deviceManager.noDesktopApps")}</p>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <h2 className="text-lg font-semibold text-gray-800 mb-3">
        {t("deviceManager.installedApps", { count: otherApps.length })}
      </h2>
      <table className="w-full text-sm text-left text-gray-800 border-collapse bg-white rounded-lg border border-gray-200">
        <thead className="border-b font-medium bg-gray-50">
          <tr>
            <th className="py-2 px-4">{t("deviceManager.colPackage")}</th>
            <th className="py-2 px-4">{t("deviceManager.colInstalledVersion")}</th>
            <th className="py-2 px-4">{t("deviceManager.colLatestVersion")}</th>
            <th className="py-2 px-4">{t("deviceManager.colStatus")}</th>
            <th className="py-2 px-4">{t("deviceManager.colActions")}</th>
          </tr>
        </thead>
        <tbody>
          {otherApps.map((app) => (
            <AppRow
              key={app.id}
              app={app}
              latestBuild={findLatestBuild(app)}
              onUninstalled={
                onRefresh ??
                (() => {
                  /* no-op */
                })
              }
              onRemoveApp={onRemoveApp}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
